import { GetterTree } from 'vuex';
import { State } from './state';
import { RootState } from '@/store';
import { config } from '@/services/config';
import { get } from 'lodash';
import { createTableHeaders, createTopTableHeaders } from '@/helpers/tables';
import {
  LeadFormField,
  LeadFormSection,
  LeadFormData,
  LeadsTableData,
} from '@/types/Leads';
import { authState } from '@/services/integrations/auth0';
import { mainRoles } from '@/enums/UserRole';
import { Banker, SingleLead } from './types';
import { createLeads, evaluateConditions } from '@/helpers/leads';
import { formatDataValue } from '@/helpers/format';
import { DataValue, KeyValuePair } from '@/types/Global';

export type Getters = {
  getLeadsLoading(state: State): boolean;
  getLeadsTableData(state: State): LeadsTableData;
  getNewLeadsCount(state: State): number;
  getLeadInformation(state: State): KeyValuePair[];
  getLeadFormData(state: State): LeadFormData;
  getBankers(state: State): Array<Banker>;
  getBankersLoading(state: State): boolean;
  getLeadsIds(state: State): string[];
  getLeadLoading(state: State): boolean;
  getLead(state: State): SingleLead;
  getUnreachableInteractions(state: State): number;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getLeadsLoading(state) {
    return state.loading;
  },
  getLeadsTableData(state) {
    const userRoles = authState.userRoles;
    const roles = userRoles.value.filter((role: string) =>
      (mainRoles as string[]).includes(role)
    );

    const leadOverviewTableConfig = get(
      config.layout,
      `tableLayouts.leadOverviewTable.${roles[0]}`,
      {}
    );
    const configTopHeaders = get(leadOverviewTableConfig, 'topHeaders', []);
    const configHeaders = get(leadOverviewTableConfig, 'mainHeaders', []);

    return {
      items: createLeads(state.leads),
      headers: createTableHeaders(configHeaders),
      topHeaders: createTopTableHeaders(configTopHeaders),
      totalResultCount: state.totalResultCount,
    };
  },
  getNewLeadsCount(state) {
    return state.newLeadsCount;
  },
  getLeadInformation(state) {
    return [
      {
        key: 'leadId',
        value: formatDataValue(state.lead.leadId),
      },
      {
        key: 'utmSource',
        value: formatDataValue(state.lead.utmSource),
      },
      {
        key: 'dateAdded',
        value: formatDataValue(state.lead.dateAdded, {
          overrideDateFormat: true,
        }),
      },
      {
        key: 'utmMedium',
        value: formatDataValue(state.lead.utmMedium),
      },
      {
        key: 'clientType',
        value: formatDataValue(state.lead.clientType),
      },
      {
        key: 'utmCampaign',
        value: formatDataValue(state.lead.utmCampaign),
      },
      {
        key: 'investmentServices',
        value: formatDataValue(state.lead.investmentServices),
      },
      {
        key: 'utmContent',
        value: formatDataValue(state.lead.utmContent),
      },
      {
        key: 'location',
        value: formatDataValue(state.lead.location),
      },
      {
        key: 'utmTerm',
        value: formatDataValue(state.lead.utmTerm),
      },
      {
        key: 'funnel',
        value: formatDataValue(state.lead.funnel),
      },
      {
        key: 'formName',
        value: formatDataValue(state.lead.formName),
      },
    ];
  },
  getLeadFormData(state) {
    const userRoles = authState.userRoles;
    const roles = userRoles.value.filter((role: string) =>
      (mainRoles as string[]).includes(role)
    );
    const leadFormSections: LeadFormSection[] = get(
      config.layout,
      `leadForm.sections`,
      []
    );
    const leadFormFields: LeadFormField[] = get(
      config.layout,
      'leadForm.fields',
      []
    );
    const lead = state.lead;

    const sectionsToDisplay = leadFormSections.filter(section =>
      section.visibility.includes(roles[0])
    );
    let fieldsToDisplay = leadFormFields
      // Filter sections based on role
      .filter(field =>
        sectionsToDisplay.map(section => section.key).includes(field.section)
      )
      // Format entries and assign values
      .map(field => {
        let value: string | number | boolean | string[] = formatDataValue(
          lead[field.key],
          {
            bypassFormatting: true,
          }
        );

        if (field.key === 'serviceOfInterest') {
          value =
            lead['serviceOfInterest'] &&
            (lead['serviceOfInterest'].map(
              (item: DataValue) => item.dataValue
            ) as string[]);
        }

        return {
          ...field,
          value,
        };
      });
    // Filter if conditions
    fieldsToDisplay = fieldsToDisplay.filter(field => {
      if (field.if != undefined) {
        return evaluateConditions(field.if, fieldsToDisplay);
      }
      return true;
    });

    const bankerFields = fieldsToDisplay.filter(field =>
      field.key.toLowerCase().endsWith('bankerid')
    );
    bankerFields.forEach(bankerField => {
      bankerField.options = state.bankers.map(banker => {
        return {
          key: banker.name,
          value: banker.banker_id,
        };
      });
    });

    return {
      sections: sectionsToDisplay,
      fields: fieldsToDisplay,
    };
  },
  getBankers(state) {
    return state.bankers;
  },
  getBankersLoading(state) {
    return state.bannkersLoading;
  },
  getLeadsIds(state) {
    return state.leads.map(lead => lead.leadId.dataValue as string);
  },
  getLeadLoading(state) {
    return state.leadLoading;
  },
  getLead(state) {
    return state.lead;
  },
  getUnreachableInteractions(state) {
    return formatDataValue(state.lead.unreachableInteractions) as number;
  },
};
